import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "antd";
import businessSuiteOfferings from "../../data/businessSuiteOfferings";

export default function Sidebar({ collapsed = false }) {
  const location = useLocation();
  const pathname = location.pathname;
  const [hoveredPath, setHoveredPath] = useState(null);

  // Get user role from localStorage
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const userRole = userInfo.role || 'company';

  // --- Configuration ---
  const primaryColor = "#3C007A"; // Deep Purple
  const activeBg = "#f0ebf8";     // Light Purple Tint for active state
  const inactiveColor = "#333"; // Cool Gray
  const hoverBg = "#f9fafb";       // Very light gray for hover

  // --- Helper for Styles ---
  const getLinkStyle = (path) => {
    const isActive = path === '/dashboard/business-suite' ? isBusinessSuiteActive : pathname === path;
    const isHovered = hoveredPath === path;

    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'flex-start',
      gap: '14px',
      padding: collapsed ? '12px' : '12px 16px',
      margin: collapsed ? '0 8px' : '0 12px',
      borderRadius: '12px',

      // Colors
      color: isActive ? primaryColor : inactiveColor,
      backgroundColor: isActive ? activeBg : (isHovered ? hoverBg : 'transparent'),

      // Text
      textDecoration: 'none',
      fontWeight: isActive ? 500 : 400,
      fontSize: '16px',
      fontFamily: "'Rubik', sans-serif",

      // Transition
      transition: 'all 0.2s ease-in-out',
      cursor: 'pointer'
    };
  };

  const getIconColor = (path) => {
    return pathname === path ? primaryColor : "#666666"; // Purple if active, lighter gray if not
  };

  const isBusinessSuiteActive = pathname === '/dashboard/business-suite'
    || businessSuiteOfferings.some(item => item.path === pathname);

  // Wrapper to add tooltip when collapsed
  const MenuItem = ({ to, icon, label, path }) => {
    const linkContent = (
      <Link
        to={to}
        style={getLinkStyle(path)}
        onMouseEnter={() => setHoveredPath(path)}
        onMouseLeave={() => setHoveredPath(null)}
      >
        {icon}
        {!collapsed && <span>{label}</span>}
      </Link>
    );

    if (collapsed) {
      return (
        <Tooltip title={label} placement="right">
          {linkContent}
        </Tooltip>
      );
    }

    return linkContent;
  };

  return (
    <div style={{ 
      paddingTop: '24px', 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '4px', 
      overflowY: 'auto', 
      maxHeight: 'calc(100vh - 80px)', 
      position: 'relative', 
      paddingBottom: '60px',
      scrollbarWidth: 'thin',
      scrollbarColor: 'white #f0f0f0'
    }}>
      <style>
        {`
          div::-webkit-scrollbar {
            width: 8px;
          }
          div::-webkit-scrollbar-track {
            background: #f0f0f0;
          }
          div::-webkit-scrollbar-thumb {
            background: white;
            border-radius: 4px;
          }
          div::-webkit-scrollbar-thumb:hover {
            background: #fafafa;
          }
        `}
      </style>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>

        {/* Dashboard */}
        {/* <li>
          <MenuItem
            to="/dashboard/company_homepage"
            path="/dashboard/company_homepage"
            label="Dashboard"
            icon={
              <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
                <path d="M7.5 2.5H3.33333C2.8731 2.5 2.5 2.8731 2.5 3.33333V9.16667C2.5 9.6269 2.8731 10 3.33333 10H7.5C7.96024 10 8.33333 9.6269 8.33333 9.16667V3.33333C8.33333 2.8731 7.96024 2.5 7.5 2.5Z" stroke={getIconColor("/dashboard/company_homepage")} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16.668 2.5H12.5013C12.0411 2.5 11.668 2.8731 11.668 3.33333V5.83333C11.668 6.29357 12.0411 6.66667 12.5013 6.66667H16.668C17.1282 6.66667 17.5013 6.29357 17.5013 5.83333V3.33333C17.5013 2.8731 17.1282 2.5 16.668 2.5Z" stroke={getIconColor("/dashboard/company_homepage")} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16.668 10H12.5013C12.0411 10 11.668 10.3731 11.668 10.8333V16.6667C11.668 17.1269 12.0411 17.5 12.5013 17.5H16.668C17.1282 17.5 17.5013 17.1269 17.5013 16.6667V10.8333C17.5013 10.3731 17.1282 10 16.668 10Z" stroke={getIconColor("/dashboard/company_homepage")} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7.5 13.3334H3.33333C2.8731 13.3334 2.5 13.7065 2.5 14.1667V16.6667C2.5 17.1269 2.8731 17.5 3.33333 17.5H7.5C7.96024 17.5 8.33333 17.1269 8.33333 16.6667V14.1667C8.33333 13.7065 7.96024 13.3334 7.5 13.3334Z" stroke={getIconColor("/dashboard/company_homepage")} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
          />
        </li> */}

        {/* Profile - Dynamic based on user role */}
        <li>
          <MenuItem
            to={
              userRole === 'association' ? '/dashboard/association_profile' :
              userRole === 'academia' ? '/dashboard/academia_profile' :
              userRole === 'startup' ? '/dashboard/startup_profile' :
              userRole === 'incubator' ? '/dashboard/incubator_profile' :
              userRole === 'investor' ? '/dashboard/investor_profile' :
              userRole === 'consultant' ? '/dashboard/consultant_profile' :
              '/dashboard/company_profile'
            }
            path={
              userRole === 'association' ? '/dashboard/association_profile' :
              userRole === 'academia' ? '/dashboard/academia_profile' :
              userRole === 'startup' ? '/dashboard/startup_profile' :
              userRole === 'incubator' ? '/dashboard/incubator_profile' :
              userRole === 'investor' ? '/dashboard/investor_profile' :
              userRole === 'consultant' ? '/dashboard/consultant_profile' :
              '/dashboard/company_profile'
            }
            label={
              userRole === 'association' ? 'Association Profile' :
              userRole === 'academia' ? 'Academia Profile' :
              userRole === 'startup' ? 'Startup Profile' :
              userRole === 'incubator' ? 'Incubator Profile' :
              userRole === 'investor' ? 'Investor Profile' :
              userRole === 'consultant' ? 'Consultant Profile' :
              'Company Profile'
            }
            icon={
              <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
                <path d="M12.082 18.3333H14.9987C15.4407 18.3333 15.8646 18.1577 16.1772 17.8451C16.4898 17.5326 16.6654 17.1087 16.6654 16.6666V5.83329L12.4987 1.66663H4.9987C4.55667 1.66663 4.13275 1.84222 3.82019 2.15478C3.50763 2.46734 3.33203 2.89127 3.33203 3.33329V6.66663" stroke={getIconColor(
                  userRole === 'association' ? '/dashboard/association_profile' :
                  userRole === 'academia' ? '/dashboard/academia_profile' :
                  userRole === 'startup' ? '/dashboard/startup_profile' :
                  userRole === 'incubator' ? '/dashboard/incubator_profile' :
                  userRole === 'investor' ? '/dashboard/investor_profile' :
                  userRole === 'consultant' ? '/dashboard/consultant_profile' :
                  '/dashboard/company_profile'
                )} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M11.668 1.66663V4.99996C11.668 5.44199 11.8436 5.86591 12.1561 6.17847C12.4687 6.49103 12.8926 6.66663 13.3346 6.66663H16.668" stroke={getIconColor(
                  userRole === 'association' ? '/dashboard/association_profile' :
                  userRole === 'academia' ? '/dashboard/academia_profile' :
                  userRole === 'startup' ? '/dashboard/startup_profile' :
                  userRole === 'incubator' ? '/dashboard/incubator_profile' :
                  userRole === 'investor' ? '/dashboard/investor_profile' :
                  userRole === 'consultant' ? '/dashboard/consultant_profile' :
                  '/dashboard/company_profile'
                )} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2.49828 10.9167C2.24146 11.0649 2.02884 11.279 1.88235 11.5368C1.73587 11.7946 1.66082 12.0868 1.66494 12.3833V15.0833C1.65529 15.3802 1.72514 15.6742 1.86726 15.935C2.00937 16.1958 2.2186 16.4139 2.47328 16.5667L4.99828 18.0833C5.25385 18.2356 5.5455 18.3166 5.84297 18.3181C6.14044 18.3195 6.43288 18.2414 6.68994 18.0917L9.16494 16.5833C9.42176 16.4351 9.63438 16.221 9.78087 15.9632C9.92735 15.7054 10.0024 15.4132 9.99828 15.1167V12.4167C10.0079 12.1198 9.93808 11.8258 9.79596 11.565C9.65385 11.3042 9.44462 11.0861 9.18994 10.9333L6.66494 9.41666C6.40937 9.26442 6.11771 9.18337 5.82025 9.1819C5.52278 9.18044 5.23033 9.25862 4.97328 9.40832L2.49828 10.9167Z" stroke={getIconColor(
                  userRole === 'association' ? '/dashboard/association_profile' :
                  userRole === 'academia' ? '/dashboard/academia_profile' :
                  userRole === 'startup' ? '/dashboard/startup_profile' :
                  userRole === 'incubator' ? '/dashboard/incubator_profile' :
                  userRole === 'investor' ? '/dashboard/investor_profile' :
                  userRole === 'consultant' ? '/dashboard/consultant_profile' :
                  '/dashboard/company_profile'
                )} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5.83203 14.1666V18.3333" stroke={getIconColor(
                  userRole === 'association' ? '/dashboard/association_profile' :
                  userRole === 'academia' ? '/dashboard/academia_profile' :
                  userRole === 'startup' ? '/dashboard/startup_profile' :
                  userRole === 'incubator' ? '/dashboard/incubator_profile' :
                  userRole === 'investor' ? '/dashboard/investor_profile' :
                  userRole === 'consultant' ? '/dashboard/consultant_profile' :
                  '/dashboard/company_profile'
                )} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.7513 11.8334L5.83464 14.1667L1.91797 11.8334" stroke={getIconColor(
                  userRole === 'association' ? '/dashboard/association_profile' :
                  userRole === 'academia' ? '/dashboard/academia_profile' :
                  userRole === 'startup' ? '/dashboard/startup_profile' :
                  userRole === 'incubator' ? '/dashboard/incubator_profile' :
                  userRole === 'investor' ? '/dashboard/investor_profile' :
                  userRole === 'consultant' ? '/dashboard/consultant_profile' :
                  '/dashboard/company_profile'
                )} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
          />
        </li>
        <li>
          <MenuItem
            to="/dashboard/contact"
            path="/dashboard/contact"
            label="Contact AID"
            icon={
              <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
                <path d="M3.33333 3.33333H16.6667C17.5833 3.33333 18.3333 4.08333 18.3333 5V15C18.3333 15.9167 17.5833 16.6667 16.6667 16.6667H3.33333C2.41667 16.6667 1.66667 15.9167 1.66667 15V5C1.66667 4.08333 2.41667 3.33333 3.33333 3.33333Z" stroke={getIconColor("/dashboard/contact")} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.3333 5L10 10.8333L1.66667 5" stroke={getIconColor("/dashboard/contact")} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
          />
        </li>
        {/* Business Suite (opens the catalog view in the main content area) */}
        <li>
          <MenuItem
            to="/dashboard/business-suite"
            path="/dashboard/business-suite"
            label="Business Suite"
            icon={
              <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
                <path d="M17.5 5.83333L10 2.5L2.5 5.83333L10 9.16667L17.5 5.83333Z" stroke={isBusinessSuiteActive ? primaryColor : "#666666"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2.5 14.1667L10 17.5L17.5 14.1667" stroke={isBusinessSuiteActive ? primaryColor : "#666666"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2.5 10L10 13.3333L17.5 10" stroke={isBusinessSuiteActive ? primaryColor : "#666666"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
          />
        </li>

        {/* Logout */}
        <li>
          <MenuItem
            to="/login"
            path="/login"
            label="Log Out"
            icon={
              <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
                <path d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5" stroke={getIconColor("/login")} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M13.332 14.1667L17.4987 10L13.332 5.83337" stroke={getIconColor("/login")} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M17.5 10H7.5" stroke={getIconColor("/login")} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
          />
        </li>

      </ul>

      {/* Powered by Bharat Online - Footer */}
      {!collapsed && (
        <div style={{
          marginTop: '16px',
          padding: '16px 8px',
          textAlign: 'center',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          fontSize: '12px',
          color: '#666666',
          fontFamily: "'Rubik', sans-serif",
          fontWeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          flexWrap: 'nowrap',
          whiteSpace: 'nowrap'
        }}>
          <span>Powered by</span>
          <img
            src="/img/bo_logo.png"
            alt="Bharat Online Logo"
            style={{
              height: '24px',
              width: 'auto',
              objectFit: 'contain',
              flexShrink: 0
            }}
          />
          <a 
            href="https://www.bharat-online.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              fontWeight: 500, 
              fontSize: '16px', 
              color: '#3C007A',
              textDecoration: 'none',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Bharat Online
          </a>
        </div>
      )}
    </div>
  );
}